import { useLayer } from '@coursera-web/statsig';
import * as Sentry from '@sentry/react';

import CdsEpicClient from 'bundles/epic/clients/CDS';
import LceCourseExperienceExperiments from 'bundles/epic/clients/LceCourseExperience';
import type { ContentSummary } from 'bundles/learner-progress/types/ContentSummary';

type ItemTypeName = ContentSummary['typeName'];

/**
 * A feature flag to guard the injectable activity UI during development and testing.
 * @returns true if https://tools.coursera.org/epic/experiment/Xvjsm0i4EfGL3A6KZpIPHQ is enabled.
 */
export const isActivityInjectionUiEnabled = (): boolean => {
  return LceCourseExperienceExperiments.preview('isActivityInjectionUiEnabled') === true;
};

/**
 * A feature flag to guard the injectable activity API during development and testing.
 * @returns true if https://tools.coursera.org/epic/experiment/97cN0ki3EfGErBK2XQAA3w is enabled.
 */
export const isActivityInjectionApiEnabled = (): boolean => {
  return LceCourseExperienceExperiments.preview('isActivityInjectionApiEnabled') === true;
};

/**
 * An A/B/C test to determine the variant of the activity injection.
 * https://tools.coursera.org/epic/experiment/kXddQUi3EfGErBK2XQAA3w
 * @returns the variant of the activity injection OR undefined if the user is not targeted for the experiment.
 */
export type ActivityInjectionVariant = ReturnType<typeof LceCourseExperienceExperiments.preview<'practiceMoment'>>;
export const previewActivityInjectionVariant = (): ActivityInjectionVariant => {
  return LceCourseExperienceExperiments.preview('practiceMoment');
};
export const impressActivityInjectionVariant = (): ActivityInjectionVariant => {
  return LceCourseExperienceExperiments.get('practiceMoment');
};

/**
 * EPIC: https://tools.coursera.org/epic/experiment/k98dJpsQEfC-lAr_4rnFJw
 * This experiment is "ended" (rollout 0), but keeping this around for easy internal access to Phase 1 for testing purposes.
 */
export const isCourseFrameRevertToPhase1Enabled = (): boolean => {
  return LceCourseExperienceExperiments.preview('isCourseFrameRevertToPhase1Enabled') === true;
};

/**
 * EPIC: https://tools.coursera.org/epic/experiment/zpXu_M_oEfCgRA4nU0VIYQ
 * @returns true if phase 3 should be shown in the UI.
 */
export const isCourseFramePhase3Enabled = (): boolean => {
  return LceCourseExperienceExperiments.preview('isCourseFramePhase3Enabled') === true;
};

/**
 * @returns true if the tool panel should be shown instead of inline tabs.
 */
export const isToolPanelEnabled = (): boolean => {
  return LceCourseExperienceExperiments.get('isToolPanelEnabled') === true;
};

/**
 * Triggers the Statsig A/A layer exposure for course experience.
 */
export const useCourseExperienceAAExposure = () => {
  const layer = useLayer('aa-ondemand-pilot');
  layer.get('Style Type', 'old');
};

// EPIC: https://tools.coursera.org/epic/experiment/E6KF0T6AEfGH5Qr_6RGxyQ
export const enableCourseItemCoverRedesign = (): boolean =>
  CdsEpicClient.get('enableCourseItemCoverRedesign') === 'test';

// Item types that always render the redesigned ItemCoverPage regardless of the flag
// (gradedCoach hardcodes RolePlayCover in GradedLearnerRolePlayItem without a flag check).
const ALWAYS_REDESIGNED_COVER_TYPES: ItemTypeName[] = ['gradedCoach'];

// Item types that render the redesigned ItemCoverPage only when the flag is on.
const FLAG_GATED_REDESIGNED_COVER_TYPES: ItemTypeName[] = [
  'coach',
  'ungradedLab',
  'gradedLab',
  'ungradedProgramming',
  'gradedProgramming',
  'ungradedLti',
  'gradedLti',
];

/** Returns true when the item type is currently showing the redesigned ItemCoverPage with its fixed mobile footer. */
export const isRedesignedCoverActive = (typeName: ItemTypeName): boolean =>
  ALWAYS_REDESIGNED_COVER_TYPES.includes(typeName) ||
  (enableCourseItemCoverRedesign() && FLAG_GATED_REDESIGNED_COVER_TYPES.includes(typeName));

type ExperimentStartDateConfigKey = 'CourseFramePhase3StartDate' | 'adaptiveLearningStartDate';

/**
 * Compares the user's enrollment time against an experiment start date stored in an Epic config.
 * The config holds an ISO date string (e.g. "2025-10-05T19:00:00.000Z"); the comparison uses `preview`
 * so it doesn't trigger an impression — callers should chain a `.get()` for the gated variant.
 *
 * @param enrollmentTime - Enrollment time (epoch ms) of the user in the current course.
 * @param configKey - Epic config key holding the experiment start date.
 * @returns true if the user enrolled strictly after the configured start date.
 */
export const isEnrolledAfterExperimentStartDate = (
  enrollmentTime: number | undefined,
  configKey: ExperimentStartDateConfigKey
): boolean => {
  const startDateString = LceCourseExperienceExperiments.preview(configKey);
  if (startDateString && typeof enrollmentTime === 'number') {
    const experimentStartDate = new Date(startDateString);
    if (isNaN(experimentStartDate.getTime())) {
      // invalid date from Epic config
      Sentry.captureException('isEnrolledAfterExperimentStartDate: Invalid date from Epic config', {
        extra: { configKey, startDateString },
      });
      return false;
    }
    const experimentStartTime = experimentStartDate.getTime();
    return enrollmentTime > experimentStartTime;
  }
  return false;
};
